import { useState } from 'react';
import DataTable from '../../components/DataTable';
import { DAY_NAMES } from '../../constants/openingHours';
import { formatDate, langValue } from '../../utils/formatters';
import { emptyPlaceForm, toPlaceForm, toPlacePayload } from '../../utils/placeForm';
import PlaceDialog from './PlaceDialog';

const PlacesPage = ({ places, filters, setFilters, loadPlaces, onSavePlace, loading }) => {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeForm, setPlaceForm] = useState(emptyPlaceForm);
  const [duplicateTargets, setDuplicateTargets] = useState(() =>
    DAY_NAMES.map((_, day) => (day + 1) % 7),
  );
  const openPlace = (place) => {
    setSelectedPlace(place);
    setPlaceForm(toPlaceForm(place));
  };
  const closePlace = () => {
    setSelectedPlace(null);
  };
  const updateNested = (section, lang, value) => {
    setPlaceForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [lang]: value,
      },
    }));
  };
  const updateDayRanges = (dayIndex, updater) => {
    setPlaceForm((current) => ({
      ...current,
      openingHours: current.openingHours.map((day) =>
        day.day === dayIndex
          ? {
              ...day,
              ranges: updater(day.ranges),
            }
          : day,
      ),
    }));
  };
  const addRange = (dayIndex) => {
    updateDayRanges(dayIndex, (ranges) => [
      ...ranges,
      {
        open: '08:00',
        close: '17:00',
        isOpen24Hours: false,
      },
    ]);
  };
  const removeRange = (dayIndex, rangeIndex) => {
    updateDayRanges(dayIndex, (ranges) => ranges.filter((_, index) => index !== rangeIndex));
  };
  const updateRange = (dayIndex, rangeIndex, field, value) => {
    updateDayRanges(dayIndex, (ranges) =>
      ranges.map((range, index) =>
        index === rangeIndex
          ? {
              ...range,
              [field]: value,
            }
          : range,
      ),
    );
  };
  const duplicateDay = (sourceDayIndex, targetDayIndex) => {
    const sourceDay = placeForm.openingHours.find((day) => day.day === sourceDayIndex);
    setPlaceForm((current) => ({
      ...current,
      openingHours: current.openingHours.map((day) =>
        day.day === targetDayIndex
          ? {
              ...day,
              ranges: sourceDay.ranges.map((range) => ({
                ...range,
              })),
            }
          : day,
      ),
    }));
  };
  const isOpen247 = placeForm.openingHours.every(
    (day) => day.ranges.length === 1 && day.ranges[0].isOpen24Hours,
  );
  const setOpen247 = (checked) => {
    setPlaceForm((current) => ({
      ...current,
      openingHours: current.openingHours.map((day) => ({
        ...day,
        ranges: checked
          ? [
              {
                open: '00:00',
                close: '00:00',
                isOpen24Hours: true,
              },
            ]
          : [],
      })),
    }));
  };
  const savePlace = async (event) => {
    event.preventDefault();
    if (!selectedPlace) return;
    await onSavePlace(selectedPlace.placeId, toPlacePayload(placeForm));
    closePlace();
  };

  const togglePlaceFlag = async (event, place, field) => {
    event.stopPropagation();
    event.preventDefault();

    await onSavePlace(place.placeId, {
      [field]: !Boolean(place[field]),
    });
  };

  const columns = [
    {
      key: 'placeId',
      label: 'Place ID',
      render: (place) => place.placeId,
    },
    {
      key: 'nameHe',
      label: 'שם',
      render: (place) => place.name?.he || '-',
    },
    {
      key: 'nameEn',
      label: 'שם סניף',
      render: (place) => place.branchName?.he || '-',
    },
    {
      key: 'city',
      label: 'עיר',
      render: (place) => langValue(place.city),
    },
    {
      key: 'country',
      label: 'מדינה',
      render: (place) => langValue(place.country),
    },
    {
      key: 'address',
      label: 'כתובת',
      render: (place) => langValue(place.address),
    },
    {
      key: 'status',
      label: 'סטטוס',
      render: (place) => <span className={`pill ${place.status}`}>{place.status}</span>,
    },
    {
      key: 'verified',
      label: 'מאומת',
      render: (place) => (
        <button
          type="button"
          className={`status-dot ${place.isVerified ? 'on' : 'off'}`}
          title={place.isVerified ? 'לחץ לביטול אימות' : 'לחץ להפעלת אימות'}
          aria-label={place.isVerified ? 'בטל אימות מקום' : 'אמת מקום'}
          disabled={loading}
          onClick={(event) => togglePlaceFlag(event, place, 'isVerified')}
        />
      ),
    },
    {
      key: 'behaviorType',
      label: 'סוג',
      render: (place) => place.behaviorType || '-',
    },
    {
      key: 'updatedAt',
      label: 'עודכן',
      render: (place) => formatDate(place.updatedAt),
    },
  ];

  return (
    <section className="work-area table-page" dir="rtl">
      <header className="section-header">
        <div>
          <h1>רשימת מקומות</h1>
          <span>{places.total} מקומות</span>
        </div>
        <div className="filters compact">
          <input
            placeholder="חיפוש"
            value={filters.search}
            onChange={(e) =>
              setFilters({
                ...filters,
                search: e.target.value,
              })
            }
          />
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: e.target.value,
              })
            }
          >
            <option value="">כל הסטטוסים</option>
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="rejected">rejected</option>
          </select>
          <select
            value={filters.isVerified}
            onChange={(e) =>
              setFilters({
                ...filters,
                isVerified: e.target.value,
              })
            }
          >
            <option value="">כל המקומות</option>
            <option value="true">מקומות מאומתים</option>
            <option value="false">מקומות לא מאומתים</option>
          </select>
          <button onClick={() => loadPlaces(1)}>סינון</button>
        </div>
      </header>

      <DataTable
        columns={columns}
        items={places.items}
        rowKey={(place) => place.placeId}
        onRowClick={openPlace}
        page={places.page}
        limit={places.limit}
        total={places.total}
        onPageChange={loadPlaces}
        loading={loading}
      />

      <PlaceDialog
        open={Boolean(selectedPlace)}
        selectedPlace={selectedPlace}
        placeForm={placeForm}
        setPlaceForm={setPlaceForm}
        updateNested={updateNested}
        addRange={addRange}
        removeRange={removeRange}
        updateRange={updateRange}
        duplicateDay={duplicateDay}
        duplicateTargets={duplicateTargets}
        setDuplicateTargets={setDuplicateTargets}
        setOpen247={setOpen247}
        isOpen247={isOpen247}
        onClose={closePlace}
        onSave={savePlace}
        loading={loading}
      />
    </section>
  );
};
export default PlacesPage;
